import { parseHtmlString } from "@/utils/helpers/html";
import { Heading, Paragraph } from "@/components/ui/Typography"
import { WebsitePageSection } from "@/types"
import EditableSection from "@/components/admin/Content/WebPages/EditableSection";
import InlinePressReleaseEditor from "@/components/admin/Content/WebPages/InlineEditors/InlinePressReleaseEditor";
import { useHomePageHighlights } from "@/lib/api/hooks/press-release";

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const HeroSection = ({ data }: { data: WebsitePageSection }) => {
  // Fetch live highlights for hero section
  const { data: highlightsData } = useHomePageHighlights();
  const highlightedPressReleases = highlightsData?.data || [];

  const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png";


  return (
    <EditableSection
      sectionTag={data.sectionTag}
      contentType={data.contentType}
      data={data}
      customEditor={InlinePressReleaseEditor}
    >
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <Paragraph className="bg-gray-50 rounded-2xl px-3 py-1 !text-sm flex items-center justify-center text-forestGreen-100 w-fit">
            Hero Section ({highlightedPressReleases.length}/3)
          </Paragraph>
          <div className="flex items-center gap-6">
            <Heading level={4}>{data.title ?? "Press Release Highlights"}</Heading>
          </div>
          <Paragraph dangerouslySetInnerHTML={parseHtmlString(data.description ?? "Featured press releases displayed in the landing page hero section.")} />
        </div>

        {/* Live Preview matching public landing page layout */}
        {highlightedPressReleases.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left Column - Main Featured Article */}
            {highlightedPressReleases[0] && (
              <div className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                {/* Position Badge */}
                <div className="inline-flex items-center gap-2">
                  <span className="px-2 py-1 bg-[#681628] text-white text-xs font-medium rounded">
                    Main Feature
                  </span>
                </div>

                {/* Cover Image */}
                <div className="relative w-full h-40 bg-gray-200 rounded overflow-hidden">
                  <img
                    src={highlightedPressReleases[0].coverImage || defaultImage}
                    alt={highlightedPressReleases[0].title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      if (e.currentTarget.src !== defaultImage) {
                        e.currentTarget.src = defaultImage;
                      }
                    }}
                  />
                </div>

                {/* Content */}
                <div className="flex flex-col gap-2">
                  <h4 className="font-semibold text-sm text-gray-900 line-clamp-2">{highlightedPressReleases[0].title}</h4>
                  {(highlightedPressReleases[0].datePublished || highlightedPressReleases[0].createdByUsername) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {highlightedPressReleases[0].datePublished && (
                        <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(highlightedPressReleases[0].datePublished)}</p>
                      )}
                      {highlightedPressReleases[0].createdByUsername && (
                        <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {highlightedPressReleases[0].createdByUsername}</p>
                      )}
                    </div>
                  )}
                  <div
                    className="text-xs text-gray-600 line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: highlightedPressReleases[0].content ?? '' }}
                  />
                </div>

                {/* Tags */}
                {highlightedPressReleases[0].articleTag && Array.isArray(highlightedPressReleases[0].articleTag) && highlightedPressReleases[0].articleTag.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {highlightedPressReleases[0].articleTag.slice(0, 2).map((tag: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Right Column - Side Articles */}
            <div className="grid gap-4">
              {/* Side Article 1 */}
              {highlightedPressReleases[1] && (
                <div className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {/* Position Badge */}
                  <div className="inline-flex items-center gap-2">
                    <span className="px-2 py-1 bg-[#681628] text-white text-xs font-medium rounded">
                      Article 2
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Cover Image */}
                    <div className="relative w-full h-24 bg-gray-200 rounded overflow-hidden">
                      <img
                        src={highlightedPressReleases[1].coverImage || defaultImage}
                        alt={highlightedPressReleases[1].title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (e.currentTarget.src !== defaultImage) {
                            e.currentTarget.src = defaultImage;
                          }
                        }}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex flex-col gap-1">
                      <h4 className="font-semibold text-sm text-gray-900 line-clamp-2">{highlightedPressReleases[1].title}</h4>
                      {(highlightedPressReleases[1].datePublished || highlightedPressReleases[1].createdByUsername) && (
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {highlightedPressReleases[1].datePublished && (
                            <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(highlightedPressReleases[1].datePublished)}</p>
                          )}
                          {highlightedPressReleases[1].createdByUsername && (
                            <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {highlightedPressReleases[1].createdByUsername}</p>
                          )}
                        </div>
                      )}
                      <div
                        className="text-xs text-gray-600 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: highlightedPressReleases[1].content ?? '' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Side Article 2 */}
              {highlightedPressReleases[2] && (
                <div className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {/* Position Badge */}
                  <div className="inline-flex items-center gap-2">
                    <span className="px-2 py-1 bg-[#681628] text-white text-xs font-medium rounded">
                      Article 3
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Cover Image */}
                    <div className="relative w-full h-24 bg-gray-200 rounded overflow-hidden">
                      <img
                        src={highlightedPressReleases[2].coverImage || defaultImage}
                        alt={highlightedPressReleases[2].title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (e.currentTarget.src !== defaultImage) {
                            e.currentTarget.src = defaultImage;
                          }
                        }}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex flex-col gap-1">
                      <h4 className="font-semibold text-sm text-gray-900 line-clamp-2">{highlightedPressReleases[2].title}</h4>
                      {(highlightedPressReleases[2].datePublished || highlightedPressReleases[2].createdByUsername) && (
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {highlightedPressReleases[2].datePublished && (
                            <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(highlightedPressReleases[2].datePublished)}</p>
                          )}
                          {highlightedPressReleases[2].createdByUsername && (
                            <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {highlightedPressReleases[2].createdByUsername}</p>
                          )}
                        </div>
                      )}
                      <div
                        className="text-xs text-gray-600 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: highlightedPressReleases[2].content ?? '' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-sm">No press release highlights selected. Click edit to select up to 3 articles.</p>
          </div>
        )}
      </div>
    </EditableSection>
  )
}

export default HeroSection